import React, { useState } from 'react';

import { StyledForm, Row } from './styledComponents';

const CONFIRMATION_TEXT = 'DELETE MY ACCOUNT';

const DeleteAccountForm = ({ handleSubmit }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  // Disable the button while the DELETE request is in flight so a double-click
  // (or slow network) can't dispatch the destructive action twice. On success
  // the saga hard-navigates and this form unmounts; on failure the saga only
  // surfaces a toast, so we re-enable submission when the user edits an input.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValid =
    currentPassword.length > 0 && confirmationText === CONFIRMATION_TEXT;

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    handleSubmit({ currentPassword, confirmationText });
  };

  return (
    <StyledForm onSubmit={onSubmit}>
      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-red-700 font-semibold mb-2">This can't be undone.</p>
        <ul className="text-red-600 text-sm list-disc list-inside space-y-1">
          <li>Your account is deleted and you're signed out</li>
          <li>Channels you own are deleted, with their galleries, photo pages and leads</li>
          {/* Counsel-verified copy. Rails C3 (#1411) rescues every Stripe error and alerts a person
              for a same-day manual cancel. See counsel opinion 2026-10-06-mb-help-deletion-wording,
              Addendum 2 (3a2385c). */}
          <li>Your subscription in Manage subscription is canceled now, not at the end of the billing period. If you pay another way, contact us first.</li>
        </ul>
      </div>

      <Row>
        <label
          htmlFor="delete-account-current-password"
          className="block text-xs font-semibold tracking-wide mb-1 uppercase"
        >
          Current password
        </label>
        <input
          id="delete-account-current-password"
          type="password"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-red-400"
          value={currentPassword}
          onChange={(e) => {
            setCurrentPassword(e.target.value);
            if (isSubmitting) setIsSubmitting(false);
          }}
          autoComplete="current-password"
        />
      </Row>

      <Row>
        <label
          htmlFor="delete-account-confirmation-text"
          className="block text-xs font-semibold tracking-wide mb-1 uppercase"
        >
          Type DELETE MY ACCOUNT to confirm
        </label>
        <input
          id="delete-account-confirmation-text"
          type="text"
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-red-400"
          value={confirmationText}
          onChange={(e) => {
            setConfirmationText(e.target.value);
            if (isSubmitting) setIsSubmitting(false);
          }}
          autoComplete="off"
          spellCheck={false}
        />
      </Row>

      <Row>
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="px-6 py-2 bg-red-600 text-white text-sm font-semibold rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700 transition-colors"
        >
          {isSubmitting ? 'Deleting…' : 'Delete my account'}
        </button>
        {isSubmitting && (
          // The saga surfaces failures only via a toast and does not notify
          // this form, so editing a field is what re-enables the button. Make
          // that recovery path visible instead of leaving a silently-stuck
          // "Deleting…" button if the request fails.
          <p className="mt-2 text-xs text-gray-500">
            Processing your request. If it doesn’t go through, edit a field
            above to try again.
          </p>
        )}
      </Row>
    </StyledForm>
  );
};

export default DeleteAccountForm;
